import {
  getMobileConfig,
  type MobileConfig,
} from '@/features/auth/utils/mobileNumber';

export const LOGIN_OTP_LENGTH = 6;
export const LOGIN_EMAIL_MAX_LENGTH = 254;

export type LoginMethod = 'password' | 'otp';

export type LoginFormValues = {
  identifier: string;
  password?: string;
  rememberMe: boolean;
};

export const emptyLoginOtp = () => new Array(LOGIN_OTP_LENGTH).fill('');

export const normalizeIdentifier = (value = '') => value.trim();

export const isEmailLike = (value: string) =>
  value.includes('@') || /[a-zA-Z]/.test(value);

export const getDigits = (value = '') => value.replace(/\D/g, '');

/** True when input looks like a regional mobile (valid first digit, digits only). */
export const isPhoneLikeIdentifier = (
  value: string,
  phone: MobileConfig = getMobileConfig(),
) => {
  const trimmed = normalizeIdentifier(value);
  if (!trimmed || isEmailLike(trimmed)) return false;
  if (!/^[\d\s+\-()]*$/.test(trimmed)) return false;
  const digits = getDigits(trimmed);
  if (!digits) return false;
  return phone.firstDigitRegex.test(digits[0] || '');
};

export const toPayloadIdentifier = (value: string) => {
  const trimmed = normalizeIdentifier(value);
  return isEmailLike(trimmed) ? trimmed : getDigits(trimmed);
};

export const validateLoginIdentifier = (
  value: string,
  phone: MobileConfig = getMobileConfig(),
) => {
  const trimmed = normalizeIdentifier(value);

  if (!trimmed) return 'Email or phone number is required';

  if (isEmailLike(trimmed)) {
    if (trimmed.length > LOGIN_EMAIL_MAX_LENGTH) {
      return `Email cannot exceed ${LOGIN_EMAIL_MAX_LENGTH} characters`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      return 'Please enter a valid email address';
    }
    return true;
  }

  const digits = getDigits(trimmed);
  if (!digits || /[^\d\s+\-()]/.test(trimmed)) {
    return 'Please enter a valid email or phone number';
  }
  // Digits that cannot be a regional mobile (wrong first digit) are treated
  // as an incomplete/invalid email, not a phone number.
  if (!phone.firstDigitRegex.test(digits[0] || '')) {
    return 'Please enter a valid email or phone number';
  }
  if (digits.length !== phone.length) {
    return `Phone number must be ${phone.length} digits`;
  }
  if (!phone.regex.test(digits)) return phone.message;
  return true;
};
